<template>
  <div class="agent-report-layout">
    <!-- Loading State -->
    <div v-if="loading" class="loading-container">
      <div class="loading-spinner"></div>
      <p>Generating report...</p>
    </div>

    <!-- Report Content -->
    <div v-else-if="reportData.success" class="report-container">
      <!-- Report Header -->
      <div class="report-header">
        <div class="header-content">
          <div class="report-title-section">
            <h1 class="report-title">Agent Performance Report</h1>
            <div class="report-subtitle">{{ reportData.report_info?.agent_name }}</div>
            <div class="report-meta">
              <span class="agent-code">{{ reportData.report_info?.agent_id }}</span> •
              <span class="department">{{ reportData.report_info?.department_name }}</span> •
              <span class="role">{{ reportData.report_info?.agent_role }}</span>
            </div>
          </div>
          <div class="print-controls">
            <button @click="printReport" class="print-btn">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path d="M19 8H5c-1.66 0-3 1.34-3 3v6h4v4h12v-4h4v-6c0-1.66-1.34-3-3-3zM16 19H8v-5h8v5zm3-7c-.55 0-1-.45-1-1s.45-1 1-1 1 .45 1 1-.45 1-1 1zm-1-9H6v4h12V3z"/>
              </svg>
              Print Report
            </button>
            <button @click="goBack" class="back-btn">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.42-1.41L7.83 13H20v-2z"/>
              </svg>
              Back to Agents
            </button>
          </div>
        </div>

        <!-- Report Period -->
        <div class="report-period">
          <div class="period-info">
            <div class="period-label">Report Period</div>
            <div class="period-dates">{{ formatPeriod() }}</div>
          </div>
          <div class="generated-info">
            <div class="generated-label">Generated</div>
            <div class="generated-date">{{ formatDate(reportData.report_info?.generated_at) }}</div>
          </div>
        </div>
      </div>

      <!-- Performance Rating -->
      <div class="performance-rating-card">
        <div class="rating-header">
          <h2 class="section-title">Overall Performance Rating</h2>
          <div class="rating-badge" :class="getRatingClass(reportData.performance_rating?.overall_rating)">
            {{ reportData.performance_rating?.overall_rating || 'N/A' }}
          </div>
        </div>
        <div class="rating-score">{{ reportData.performance_rating?.rating_score || 0 }}/100</div>
        <div class="rating-explanation">{{ reportData.performance_rating?.rating_explanation }}</div>
      </div>

      <!-- Key Metrics -->
      <div class="metrics-section">
        <h2 class="section-title">Performance Summary</h2>
        <div class="metrics-grid">
          <div class="metric-card conversations">
            <div class="metric-icon">📞</div>
            <div class="metric-content">
              <div class="metric-number">{{ reportData.performance_summary?.total_conversations || 0 }}</div>
              <div class="metric-label">Total Conversations</div>
              <div class="metric-breakdown">
                {{ reportData.performance_summary?.call_conversations || 0 }} calls •
                {{ reportData.performance_summary?.whatsapp_conversations || 0 }} WhatsApp
              </div>
            </div>
          </div>

          <div class="metric-card satisfaction">
            <div class="metric-icon">😊</div>
            <div class="metric-content">
              <div class="metric-number">{{ reportData.performance_summary?.avg_satisfaction_score || 0 }}%</div>
              <div class="metric-label">Avg Satisfaction Score</div>
              <div class="metric-comparison" :class="getComparisonClass(reportData.team_comparison?.satisfaction_vs_team)">
                {{ formatComparison(reportData.team_comparison?.satisfaction_vs_team) }} vs team
              </div>
            </div>
          </div>

          <div class="metric-card performance">
            <div class="metric-icon">⚡</div>
            <div class="metric-content">
              <div class="metric-number">{{ reportData.performance_summary?.avg_performance_score || 0 }}%</div>
              <div class="metric-label">Performance Score</div>
              <div class="metric-comparison" :class="getComparisonClass(reportData.team_comparison?.performance_vs_team)">
                {{ formatComparison(reportData.team_comparison?.performance_vs_team) }} vs team
              </div>
            </div>
          </div>

          <div class="metric-card risk">
            <div class="metric-icon">⚠️</div>
            <div class="metric-content">
              <div class="metric-number">{{ reportData.performance_summary?.avg_escalation_risk || 0 }}%</div>
              <div class="metric-label">Escalation Risk</div>
              <div class="metric-comparison" :class="getComparisonClass(-reportData.team_comparison?.escalation_risk_vs_team)">
                {{ formatComparison(reportData.team_comparison?.escalation_risk_vs_team) }} vs team
              </div>
            </div>
          </div>

          <div class="metric-card fcr">
            <div class="metric-icon">✅</div>
            <div class="metric-content">
              <div class="metric-number">{{ reportData.performance_summary?.first_contact_resolution_rate || 0 }}%</div>
              <div class="metric-label">First Contact Resolution</div>
              <div class="metric-comparison" :class="getComparisonClass(reportData.team_comparison?.fcr_vs_team)">
                {{ formatComparison(reportData.team_comparison?.fcr_vs_team) }} vs team
              </div>
            </div>
          </div>

          <div class="metric-card response">
            <div class="metric-icon">⏱️</div>
            <div class="metric-content">
              <div class="metric-number">{{ reportData.performance_summary?.avg_response_time || 0 }}s</div>
              <div class="metric-label">Avg Response Time</div>
              <div class="metric-comparison" :class="getComparisonClass(-reportData.team_comparison?.response_time_vs_team)">
                {{ formatComparison(reportData.team_comparison?.response_time_vs_team) }} vs team
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Performance Breakdown -->
      <div class="breakdown-section">
        <h2 class="section-title">Performance Breakdown</h2>
        <div class="breakdown-grid">
          <div class="breakdown-item">
            <div class="breakdown-label">Empathy Score</div>
            <div class="breakdown-value">{{ reportData.performance_summary?.avg_empathy_score || 0 }}%</div>
            <div class="breakdown-bar">
              <div class="breakdown-fill empathy" :style="{ width: (reportData.performance_summary?.avg_empathy_score || 0) + '%' }"></div>
            </div>
          </div>

          <div class="breakdown-item">
            <div class="breakdown-label">Compliance Score</div>
            <div class="breakdown-value">{{ reportData.performance_summary?.avg_compliance_score || 0 }}%</div>
            <div class="breakdown-bar">
              <div class="breakdown-fill compliance" :style="{ width: (reportData.performance_summary?.avg_compliance_score || 0) + '%' }"></div>
            </div>
          </div>

          <div class="breakdown-item">
            <div class="breakdown-label">Product Knowledge</div>
            <div class="breakdown-value">{{ reportData.performance_summary?.avg_product_knowledge || 0 }}%</div>
            <div class="breakdown-bar">
              <div class="breakdown-fill knowledge" :style="{ width: (reportData.performance_summary?.avg_product_knowledge || 0) + '%' }"></div>
            </div>
          </div>
        </div>
      </div>

      <!-- Daily Trends -->
      <div v-if="reportData.daily_performance_trends?.length" class="trends-section">
        <h2 class="section-title">Daily Performance Trends</h2>
        <div class="trends-table">
          <table>
            <thead>
              <tr>
                <th>Date</th>
                <th>Conversations</th>
                <th>Avg Satisfaction</th>
                <th>Performance</th>
                <th>Risk</th>
                <th>Satisfied Customers</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="trend in reportData.daily_performance_trends" :key="trend.date">
                <td class="date-cell">{{ formatDate(trend.date) }}</td>
                <td class="number-cell">{{ trend.conversations }}</td>
                <td class="percentage-cell">{{ trend.avg_satisfaction }}%</td>
                <td class="percentage-cell">{{ trend.avg_performance }}%</td>
                <td class="percentage-cell risk-cell">{{ trend.avg_escalation_risk }}%</td>
                <td class="number-cell">{{ trend.satisfied_customers }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Two-Column Layout -->
      <div class="two-column-section">
        <!-- Topics & Issues -->
        <div class="column-left">
          <!-- Most Handled Topics -->
          <div v-if="reportData.most_handled_topics?.length" class="topics-section">
            <h2 class="section-title">Most Handled Topics</h2>
            <div class="topics-list">
              <div v-for="topic in reportData.most_handled_topics" :key="topic.topic" class="topic-item">
                <div class="topic-name">{{ topic.topic }}</div>
                <div class="topic-count">{{ topic.count }}</div>
              </div>
            </div>
          </div>

          <!-- Common Issues -->
          <div v-if="reportData.common_issues?.length" class="issues-section">
            <h2 class="section-title">Common Issues</h2>
            <div class="issues-list">
              <div v-for="issue in reportData.common_issues" :key="issue.issue" class="issue-item">
                <div class="issue-name">{{ issue.issue }}</div>
                <div class="issue-count">{{ issue.count }}</div>
              </div>
            </div>
          </div>
        </div>

        <!-- Highlights & Suggestions -->
        <div class="column-right">
          <!-- Performance Highlights -->
          <div v-if="reportData.performance_highlights?.length" class="highlights-section">
            <h2 class="section-title">Performance Highlights</h2>
            <div class="highlights-list">
              <div v-for="highlight in reportData.performance_highlights" :key="highlight.highlight" class="highlight-item">
                <div class="highlight-text">{{ highlight.highlight }}</div>
                <div class="highlight-frequency">{{ highlight.frequency }}x</div>
              </div>
            </div>
          </div>

          <!-- Improvement Suggestions -->
          <div v-if="reportData.improvement_suggestions?.length" class="suggestions-section">
            <h2 class="section-title">Improvement Suggestions</h2>
            <div class="suggestions-list">
              <div v-for="suggestion in reportData.improvement_suggestions" :key="suggestion.suggestion" class="suggestion-item">
                <div class="suggestion-text">{{ suggestion.suggestion }}</div>
                <div class="suggestion-frequency">{{ suggestion.frequency }}x</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Critical Conversations -->
      <div v-if="reportData.critical_conversations?.length" class="critical-section">
        <h2 class="section-title">Critical Conversations</h2>
        <div class="critical-conversations">
          <div v-for="conversation in reportData.critical_conversations" :key="conversation.id" class="critical-item">
            <div class="critical-header">
              <div class="critical-info">
                <div class="critical-phone">{{ conversation.customer_phone }}</div>
                <div class="critical-date">{{ formatDate(conversation.created_at) }}</div>
              </div>
              <div class="critical-scores">
                <div class="score-item risk">
                  <div class="score-label">Risk</div>
                  <div class="score-value">{{ Math.round(conversation.escalation_risk_score) }}%</div>
                </div>
                <div class="score-item satisfaction">
                  <div class="score-label">Satisfaction</div>
                  <div class="score-value">{{ Math.round(conversation.customer_satisfaction_score) }}%</div>
                </div>
                <div class="score-status" :class="conversation.issue_resolution_status">
                  {{ conversation.issue_resolution_status }}
                </div>
              </div>
            </div>
            <div class="critical-summary">{{ conversation.ai_summary }}</div>
          </div>
        </div>
      </div>
    </div>

    <!-- Error State -->
    <div v-else class="error-container">
      <div class="error-icon">⚠️</div>
      <h2>Failed to Generate Report</h2>
      <p>{{ reportData.message || 'An error occurred while generating the report.' }}</p>
      <button @click="goBack" class="back-btn">Back to Agents</button>
    </div>
  </div>
</template>

<script>
import { DashboardService } from '../services/dashboard'

export default {
  name: 'AgentReportView',
  data () {
    return {
      reportData: {
        success: false
      },
      loading: true
    }
  },
  async mounted () {
    await this.generateReport()
  },
  methods: {
    async generateReport () {
      try {
        this.loading = true
        const agentId = this.$route.params.agentId
        const params = {}

        // Get query parameters for filtering
        const query = this.$route.query
        if (query.period) params.period = query.period
        if (query.date_from) params.date_from = query.date_from
        if (query.date_to) params.date_to = query.date_to

        this.reportData = await DashboardService.getAgentReport(agentId, params)
      } catch (error) {
        console.error('Error generating report:', error)
        this.reportData = {
          success: false,
          message: 'Failed to generate report: ' + error.message
        }
      } finally {
        this.loading = false
      }
    },

    printReport () {
      window.print()
    },

    goBack () {
      this.$router.push('/agents')
    },

    formatPeriod () {
      if (!this.reportData.report_info) return 'N/A'
      const { date_from: dateFrom, date_to: dateTo } = this.reportData.report_info
      if (!dateFrom || !dateTo) return 'N/A'
      return `${this.formatDate(dateFrom)} - ${this.formatDate(dateTo)}`
    },

    formatDate (dateString) {
      if (!dateString) return 'N/A'
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      })
    },

    getRatingClass (rating) {
      if (!rating) return 'unknown'
      return rating.toLowerCase()
    },

    getComparisonClass (value) {
      if (value > 0) return 'positive'
      if (value < 0) return 'negative'
      return 'neutral'
    },

    formatComparison (value) {
      if (!value || value === 0) return '0%'
      const sign = value > 0 ? '+' : ''
      return `${sign}${value}%`
    }
  }
}
</script>

<style scoped>
.agent-report-layout {
  min-height: 100vh;
  background: #ffffff;
  color: #1f2937;
}

/* Print Styles - A4 Optimized */
@media print {
  @page {
    size: A4;
    margin: 1.5cm 1.2cm;
  }

  * {
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }

  .print-controls {
    display: none !important;
  }

  .agent-report-layout {
    background: white !important;
    color: black !important;
    font-size: 11px !important;
    line-height: 1.3 !important;
  }

  .report-container {
    padding: 0 !important;
    max-width: none !important;
    width: 100% !important;
  }

  /* Header Optimization */
  .report-header {
    margin-bottom: 20px !important;
    padding-bottom: 15px !important;
  }

  .report-title {
    font-size: 22px !important;
    margin-bottom: 4px !important;
  }

  .report-subtitle {
    font-size: 16px !important;
    margin-bottom: 4px !important;
  }

  .report-meta {
    font-size: 10px !important;
  }

  .header-content {
    margin-bottom: 15px !important;
  }

  .report-period {
    padding: 10px 12px !important;
    gap: 20px !important;
  }

  /* Performance Rating */
  .performance-rating-card {
    padding: 16px !important;
    margin-bottom: 20px !important;
    background: white !important;
    color: black !important;
    border: 1px solid #ddd !important;
  }

  .rating-score {
    font-size: 32px !important;
    margin-bottom: 4px !important;
    color: #667eea !important;
  }

  .rating-badge {
    background: #f0f0f0 !important;
    color: #333 !important;
    border: 1px solid #ddd !important;
  }

  .rating-explanation {
    font-size: 12px !important;
    color: #666 !important;
  }

  /* Section Titles */
  .section-title {
    font-size: 14px !important;
    margin: 0 0 12px !important;
    padding-bottom: 4px !important;
    page-break-after: avoid;
    border-bottom: 1px solid #e5e7eb !important;
  }

  /* Metrics Grid - Compact for A4 */
  .metrics-section {
    margin-bottom: 20px !important;
  }

  .metrics-grid {
    display: grid !important;
    grid-template-columns: repeat(3, 1fr) !important;
    gap: 12px !important;
  }

  .metric-card {
    padding: 12px !important;
    font-size: 10px !important;
    background: #f9fafb !important;
    border-left-width: 2px !important;
  }

  .metric-icon {
    font-size: 18px !important;
  }

  .metric-number {
    font-size: 16px !important;
    line-height: 1.1 !important;
  }

  .metric-label {
    font-size: 9px !important;
    margin: 2px 0 !important;
  }

  .metric-breakdown,
  .metric-comparison {
    font-size: 8px !important;
  }

  /* Performance Breakdown */
  .breakdown-section {
    margin-bottom: 20px !important;
  }

  .breakdown-grid {
    display: grid !important;
    grid-template-columns: repeat(3, 1fr) !important;
    gap: 12px !important;
  }

  .breakdown-item {
    padding: 10px !important;
    background: #f9fafb !important;
  }

  .breakdown-label {
    font-size: 10px !important;
    margin-bottom: 4px !important;
  }

  .breakdown-value {
    font-size: 14px !important;
    margin-bottom: 6px !important;
  }

  .breakdown-bar {
    height: 6px !important;
  }

  /* Trends Table */
  .trends-section {
    margin-bottom: 20px !important;
    page-break-inside: avoid;
  }

  .trends-table {
    font-size: 9px !important;
  }

  .trends-table th {
    padding: 6px 8px !important;
    font-size: 9px !important;
    background: #f9fafb !important;
  }

  .trends-table td {
    padding: 4px 8px !important;
  }

  /* Two Column Layout - Stack on print */
  .two-column-section {
    display: block !important;
    margin-bottom: 20px !important;
  }

  .column-left,
  .column-right {
    width: 100% !important;
    margin-bottom: 15px !important;
  }

  .topics-section,
  .issues-section,
  .highlights-section,
  .suggestions-section {
    margin-bottom: 15px !important;
    page-break-inside: avoid;
  }

  .topics-list,
  .issues-list,
  .highlights-list,
  .suggestions-list {
    padding: 8px !important;
    background: #f9fafb !important;
  }

  .topic-item,
  .issue-item,
  .highlight-item,
  .suggestion-item {
    padding: 4px 0 !important;
    font-size: 10px !important;
  }

  .topic-count,
  .issue-count,
  .highlight-frequency,
  .suggestion-frequency {
    font-size: 9px !important;
    padding: 2px 6px !important;
    background: #e5e7eb !important;
  }

  /* Critical Conversations */
  .critical-section {
    margin-bottom: 20px !important;
    page-break-inside: avoid;
  }

  .critical-item {
    padding: 12px !important;
    margin-bottom: 8px !important;
    background: #fef2f2 !important;
    border: 1px solid #fecaca !important;
    page-break-inside: avoid;
  }

  .critical-header {
    margin-bottom: 6px !important;
  }

  .critical-phone {
    font-size: 10px !important;
    margin-bottom: 2px !important;
  }

  .critical-date {
    font-size: 8px !important;
  }

  .critical-scores {
    gap: 8px !important;
  }

  .score-label {
    font-size: 8px !important;
  }

  .score-value {
    font-size: 10px !important;
  }

  .score-status {
    font-size: 8px !important;
    padding: 2px 4px !important;
  }

  .critical-summary {
    font-size: 10px !important;
    line-height: 1.3 !important;
  }

  /* Page Break Controls */
  .performance-rating-card,
  .metrics-section,
  .breakdown-section {
    page-break-inside: avoid;
  }

  .trends-section,
  .critical-section {
    page-break-inside: avoid;
  }

  /* Ensure proper spacing between sections */
  .section-title:not(:first-child) {
    margin-top: 20px !important;
  }
}

/* Loading & Error States */
.loading-container, .error-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  padding: 32px;
  text-align: center;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #e5e7eb;
  border-top: 4px solid #3b82f6;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 16px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.error-icon {
  font-size: 48px;
  margin-bottom: 16px;
}

/* Report Container */
.report-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 32px;
  background: white;
  color: #1f2937;
}

/* Header */
.report-header {
  margin-bottom: 32px;
  padding-bottom: 24px;
  border-bottom: 2px solid #e5e7eb;
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 24px;
}

.report-title {
  font-size: 32px;
  font-weight: 800;
  color: #1f2937;
  margin: 0 0 8px;
}

.report-subtitle {
  font-size: 24px;
  font-weight: 600;
  color: #3b82f6;
  margin-bottom: 8px;
}

.report-meta {
  font-size: 14px;
  color: #6b7280;
}

.agent-code {
  font-family: monospace;
  background: #f3f4f6;
  padding: 2px 6px;
  border-radius: 4px;
}

.print-controls {
  display: flex;
  gap: 12px;
}

.print-btn, .back-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  background: white;
  color: #374151;
  cursor: pointer;
  transition: all 0.2s;
  text-decoration: none;
  font-size: 14px;
  font-weight: 500;
}

.print-btn:hover {
  background: #3b82f6;
  color: white;
  border-color: #3b82f6;
}

.back-btn:hover {
  background: #f9fafb;
  border-color: #9ca3af;
}

.report-period {
  display: flex;
  gap: 32px;
  background: #f9fafb;
  padding: 16px 20px;
  border-radius: 8px;
}

.period-info, .generated-info {
  text-align: center;
}

.period-label, .generated-label {
  font-size: 12px;
  color: #6b7280;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 4px;
}

.period-dates, .generated-date {
  font-size: 16px;
  font-weight: 600;
  color: #1f2937;
}

/* Performance Rating */
.performance-rating-card {
  background: white;
  color: var(--text-primary);
  padding: 24px;
  border-radius: 12px;
  text-align: center;
  margin-bottom: 32px;
  border: 1px solid var(--border-primary);
  position: relative;
}

.rating-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.rating-badge {
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 14px;
  font-weight: 700;
  text-transform: uppercase;
}

.rating-badge.excellent {
  background: #10b981;
  color: white;
}

.rating-badge.good {
  background: #3b82f6;
  color: white;
}

.rating-badge.average {
  background: #f59e0b;
  color: white;
}

.rating-badge.poor {
  background: #ef4444;
  color: white;
}

.rating-score {
  font-size: 48px;
  font-weight: 800;
  margin-bottom: 8px;
  color: #667eea;
}

.rating-explanation {
  font-size: 16px;
  color: var(--text-secondary);
}

/* Section Titles */
.section-title {
  font-size: 20px;
  font-weight: 700;
  color: #1f2937;
  margin: 0 0 20px;
  padding-bottom: 8px;
  border-bottom: 1px solid #e5e7eb;
}

/* Metrics Grid */
.metrics-section {
  margin-bottom: 32px;
}

.metrics-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 20px;
}

.metric-card {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 24px;
  background: #f9fafb;
  border-radius: 12px;
  border-left: 4px solid;
}

.metric-card.conversations { border-left-color: #6366f1; }
.metric-card.satisfaction { border-left-color: #10b981; }
.metric-card.performance { border-left-color: #3b82f6; }
.metric-card.risk { border-left-color: #ef4444; }
.metric-card.fcr { border-left-color: #8b5cf6; }
.metric-card.response { border-left-color: #f59e0b; }

.metric-icon {
  font-size: 32px;
}

.metric-content {
  flex: 1;
}

.metric-number {
  font-size: 24px;
  font-weight: 800;
  color: #1f2937;
  line-height: 1;
}

.metric-label {
  font-size: 14px;
  color: #6b7280;
  margin: 4px 0;
  font-weight: 500;
}

.metric-breakdown, .metric-comparison {
  font-size: 12px;
  color: #9ca3af;
}

.metric-comparison.positive { color: #10b981; }
.metric-comparison.negative { color: #ef4444; }
.metric-comparison.neutral { color: #6b7280; }

/* Performance Breakdown */
.breakdown-section {
  margin-bottom: 32px;
}

.breakdown-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
}

.breakdown-item {
  padding: 20px;
  background: #f9fafb;
  border-radius: 12px;
}

.breakdown-label {
  font-size: 14px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 8px;
}

.breakdown-value {
  font-size: 20px;
  font-weight: 700;
  color: #1f2937;
  margin-bottom: 12px;
}

.breakdown-bar {
  height: 8px;
  background: #e5e7eb;
  border-radius: 4px;
  overflow: hidden;
}

.breakdown-fill {
  height: 100%;
  transition: width 0.6s ease;
}

.breakdown-fill.empathy { background: linear-gradient(90deg, #10b981, #059669); }
.breakdown-fill.compliance { background: linear-gradient(90deg, #3b82f6, #2563eb); }
.breakdown-fill.knowledge { background: linear-gradient(90deg, #8b5cf6, #7c3aed); }

/* Trends Table */
.trends-section {
  margin-bottom: 32px;
}

.trends-table {
  background: white;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  overflow: hidden;
}

.trends-table table {
  width: 100%;
  border-collapse: collapse;
}

.trends-table th {
  background: #f9fafb;
  padding: 12px 16px;
  text-align: left;
  font-weight: 600;
  color: #374151;
  border-bottom: 1px solid #e5e7eb;
}

.trends-table td {
  padding: 12px 16px;
  border-bottom: 1px solid #f3f4f6;
}

.date-cell { color: #6b7280; }
.number-cell { font-weight: 600; text-align: center; }
.percentage-cell { font-weight: 600; text-align: center; }
.risk-cell { color: #ef4444; }

/* Two Column Layout */
.two-column-section {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 32px;
  margin-bottom: 32px;
}

.topics-section, .issues-section, .highlights-section, .suggestions-section {
  margin-bottom: 24px;
}

.topics-list, .issues-list, .highlights-list, .suggestions-list {
  background: #f9fafb;
  border-radius: 8px;
  padding: 16px;
}

.topic-item, .issue-item, .highlight-item, .suggestion-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid #e5e7eb;
}

.topic-item:last-child, .issue-item:last-child,
.highlight-item:last-child, .suggestion-item:last-child {
  border-bottom: none;
}

.topic-name, .issue-name, .highlight-text, .suggestion-text {
  flex: 1;
  font-size: 14px;
  color: #374151;
}

.topic-count, .issue-count, .highlight-frequency, .suggestion-frequency {
  font-weight: 600;
  color: #6b7280;
  font-size: 12px;
  background: #e5e7eb;
  padding: 4px 8px;
  border-radius: 12px;
}

/* Critical Conversations */
.critical-section {
  margin-bottom: 32px;
}

.critical-conversations {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.critical-item {
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 12px;
  padding: 20px;
}

.critical-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 12px;
}

.critical-phone {
  font-family: monospace;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 4px;
}

.critical-date {
  font-size: 12px;
  color: #6b7280;
}

.critical-scores {
  display: flex;
  gap: 12px;
  align-items: center;
}

.score-item {
  text-align: center;
}

.score-label {
  font-size: 10px;
  color: #6b7280;
  text-transform: uppercase;
  font-weight: 500;
}

.score-value {
  font-size: 14px;
  font-weight: 700;
}

.score-item.risk .score-value { color: #ef4444; }
.score-item.satisfaction .score-value { color: #10b981; }

.score-status {
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
}

.score-status.resolved { background: rgba(16, 185, 129, 0.1); color: #059669; }
.score-status.partial { background: rgba(245, 158, 11, 0.1); color: #d97706; }
.score-status.unresolved { background: rgba(239, 68, 68, 0.1); color: #dc2626; }
.score-status.escalated { background: rgba(99, 102, 241, 0.1); color: #4f46e5; }

.critical-summary {
  font-size: 14px;
  line-height: 1.5;
  color: #374151;
}

/* Mobile Responsiveness */
@media (max-width: 768px) {
  .report-container {
    padding: 16px;
  }

  .header-content {
    flex-direction: column;
    gap: 16px;
  }

  .report-title {
    font-size: 24px;
  }

  .report-subtitle {
    font-size: 18px;
  }

  .metrics-grid {
    grid-template-columns: 1fr;
  }

  .two-column-section {
    grid-template-columns: 1fr;
    gap: 16px;
  }

  .critical-header {
    flex-direction: column;
    gap: 8px;
  }

  .critical-scores {
    align-self: flex-start;
  }
}
</style>
